import { PHOTO_QUALITY, PHOTO_MAX_SIZE } from '@/global';

// https://gist.github.com/sh-ravan/9da7fc2724fa19cb3647cc8354fca7fe
export async function rotate(srcBase64: string, degrees = 90) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const image = new Image();

  if (!ctx) {
    return srcBase64;
  }

  image.src = srcBase64;
  await image.decode();

  canvas.width = degrees % 180 === 0 ? image.width : image.height;
  canvas.height = degrees % 180 === 0 ? image.height : image.width;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(image, image.width / -2, image.height / -2);

  return canvas.toDataURL();
}

export interface CompressedImage {
  dataUrl: string;
  type: string;
}

/**
 * Codifica o canvas em WebP; quando o navegador não gera WebP
 * (ex.: Safari, que devolve PNG), usa JPEG.
 * @param canvas canvas com a imagem
 */
export function canvasToCompressedImage(
  canvas: HTMLCanvasElement,
): CompressedImage {
  const webp = canvas.toDataURL('image/webp', PHOTO_QUALITY);

  if (webp.startsWith('data:image/webp')) {
    return { dataUrl: webp, type: 'image/webp' };
  }

  return {
    dataUrl: canvas.toDataURL('image/jpeg', PHOTO_QUALITY),
    type: 'image/jpeg',
  };
}

/**
 * Redimensiona e comprime a imagem
 * @param file imagem a ser comprimida
 * @param maxSize tamanho máximo do maior lado da imagem
 */
export function compressImage(
  file: Blob,
  maxSize = PHOTO_MAX_SIZE,
): Promise<CompressedImage> {
  const reader = new FileReader();
  const image = new Image();
  const canvas = document.createElement('canvas');

  const resize = () => {
    let { width, height } = image;

    if (width > height && width > maxSize) {
      height *= maxSize / width;
      width = maxSize;
    } else if (height > maxSize) {
      width *= maxSize / height;
      height = maxSize;
    }

    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(image, 0, 0, width, height);

    return canvasToCompressedImage(canvas);
  };

  return new Promise<CompressedImage>((resolve) => {
    reader.onload = (event) => {
      image.onload = () => resolve(resize());
      image.src = event.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}
